// ----------------------------------------------------------------------------------------------------
import * as React from 'react';
import _ from 'lodash';
// ----------------------------------------------------------------------------------------------------
import RaisedButton from "material-ui/RaisedButton";
// ----------------------------------------------------------------------------------------------------
import {
    IconURIPath,
    IWidgetThingsProps,
    PartialIWidgetThingsProps,
    WidgetLocalizationKeys
} from '../WidgetsThingsStructs';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
// ----------------------------------------------------------------------------------------------------
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThing, WebThingPort } from '@API/WebThing';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { isDefined, isFunction, IsNullOrEmpty } from '@Utils/Micro';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { StateStorage } from '@Utils/StateStorage';
import type { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { SettingsElementHighlights, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
// ----------------------------------------------------------------------------------------------------
const elementsValidator = () => JsonSchemaValidator.GetInstance(
    'DynamicFormElementsPortValidator',
    {
        type: 'array',
        items: {
            type: 'object',
            properties: {
                name: { type: 'string' },
                elements: {
                    type: 'array',
                    items: {
                        type: 'object',
                        nullable: true,
                        properties: {
                            type: { type: ['number', 'string'] },
                            label: { type: 'string' },
                            dataPath: { type: 'string' }
                        },
                        required: [
                            'type',
                            //'label',
                            //'dataPath'
                        ]
                    }
                }
            }
        }
    }
);
// ----------------------------------------------------------------------------------------------------
const dataValidator = () => JsonSchemaValidator.GetInstance('DynamicFormDataPortValidator', { type: 'object' });
// ----------------------------------------------------------------------------------------------------
enum UnitOfTime {
    sec,
    min,
    hour
}
// ----------------------------------------------------------------------------------------------------
interface IOnSelectChangeSendData {
    jsonName: string;
    elementData: string;
    previousData: string;
    formData: object;
}
// ----------------------------------------------------------------------------------------------------
interface IAutoSubmit {
    enable: boolean;
    unitOfTime: UnitOfTime;
    timeInterval: number;
}
// ----------------------------------------------------------------------------------------------------
interface ISubmitButton {
    visible: boolean;
    label: string;
}
// ----------------------------------------------------------------------------------------------------
interface IDynamicFormSettings {
    fontColor: string;
    backgroundColor: string;
    cancelButton: boolean;
    submitButton: ISubmitButton;
    autoSubmit: IAutoSubmit;
    gridGap: number;
    gridColumns: number;
    padding: number;
    onHoverHighlight?: boolean;
}
// ----------------------------------------------------------------------------------------------------
type IData = { [key: string]: any };
// ----------------------------------------------------------------------------------------------------
interface IDynamicFormState {
    elements: SettingsGroup[];
    UnsavedData: boolean;
}
// ----------------------------------------------------------------------------------------------------
class DynamicForm extends React.Component<PartialIWidgetThingsProps<IDynamicFormSettings>, IDynamicFormState> {
    // ------------------------------------------------------------------------------------------------
    private _intervalID: number;
    // ------------------------------------------------------------------------------------------------
    private _storage: StateStorage;
    // ------------------------------------------------------------------------------------------------
    private _tmpData: IData;
    private _lastSendData: IData;
    private _dataTimestamp = 0;
    // ------------------------------------------------------------------------------------------------
    constructor(props: PartialIWidgetThingsProps<IDynamicFormSettings>) {
        super(props);

        // Session storage initialization.
        this._storage = StateStorage.GetInstance();
        this._dataTimestamp = 0;
        this._lastSendData = null;
        this._tmpData = this.storageData;
        this.state = {
            // Elements initialization.
            elements: null,
            // Any unsaved data?
            UnsavedData: isDefined(this._tmpData)
        };
    }
    // ------------------------------------------------------------------------------------------------
    get storageKey(): string { return this.props.ThingKey; }
    // ------------------------------------------------------------------------------------------------
    get storageData() {
        if (isDefined(this._tmpData))
            return this._tmpData;
        return this._storage.GetState(this.storageKey);
    }
    // ------------------------------------------------------------------------------------------------
    get storageTs(): number {
        // Get timestamp for session storage data.
        const data = this._storage.GetStateWithTimestamp(this.storageKey);
        return isDefined(data) ? data.timestamp : null;
    }
    // ------------------------------------------------------------------------------------------------
    get autoSubmit(): boolean { return this.autoSubmitConfig.enable; }
    // ------------------------------------------------------------------------------------------------
    get autoSubmitConfig(): IAutoSubmit { return this.props.WidgetData.autoSubmit; }
    // ------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.checkForStateUpdates();
        if (this.autoSubmit)
            this.setTimeInterval();
    }
    // ------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        if (this.autoSubmit)
            this.clearTimeInterval();
        if (isDefined(this._tmpData))
            this._storage.SetState(this.storageKey, this._tmpData);
    }
    // ------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: IWidgetThingsProps<IDynamicFormSettings>) {
        // Check for available updates (if needs to).
        if (this.props.PortVersion != prevProps.PortVersion)
            this.checkForStateUpdates(this.props.PortsStates);

        // Auto submit config changed?
        if (!_.isEqual(this.props.WidgetData.autoSubmit, prevProps.WidgetData.autoSubmit)) {
            // Cancel the auto submit.
            this.clearTimeInterval();

            // Reset the auto submit? 
            const config = this.props.WidgetData.autoSubmit;
            if (config.enable)
                this.setTimeInterval(config);
        }
    }
    // ------------------------------------------------------------------------------------------------
    onDataChange = data => {
        // Save data to session storage.
        this._tmpData = data;
        let unsavedData = !_.isEqual(this._tmpData, this._lastSendData);
        if (unsavedData != this.state.UnsavedData)
            this.setState({ UnsavedData: unsavedData });
    }
    // ------------------------------------------------------------------------------------------------
    clearStorage = () => {
        // Remove data from session storage.
        this._storage.RemoveState(this.storageKey);
        this.setState({ UnsavedData: false });
    }
    // ------------------------------------------------------------------------------------------------
    setTimeInterval = (config: IAutoSubmit = this.autoSubmitConfig) => {
        // Submit form with a fixed time delay.
        this._intervalID = window.setInterval(() => {
            // Break out if there are no changes.
            if (!_.isEqual(this._lastSendData, this._tmpData))
                this.handleSubmitClick(true);
        }, this.getTimeDelay(config));
    }
    // ------------------------------------------------------------------------------------------------
    clearTimeInterval = () => {
        // Cancel the timed submit.
        window.clearInterval(this._intervalID);
    }
    // ------------------------------------------------------------------------------------------------
    getTimeDelay = (config: IAutoSubmit): number => {
        // Set the coefficient for conversion (to ms). 
        let coefficient: number = 0;
        switch (config.unitOfTime) {
            case UnitOfTime.sec:
                coefficient = 1000;
                break;
            case UnitOfTime.min:
                coefficient = 60000;
                break;
            case UnitOfTime.hour:
                coefficient = 3600000;
                break;
            default:
                break;
        }
        // Return the time delay.
        return config.timeInterval * coefficient;
    }
    // ------------------------------------------------------------------------------------------------
    handleSubmitClick = (auto: boolean = false) => {
        let data = this.storageData;

        // Submit the form.
        const portKey = auto ? PortKey.autoSubmit : PortKey.submit;
        this.props.SendPortEvent(portKey, JSON.stringify(data));

        // Clear session storage and update data.
        this.clearStorage();
        this._lastSendData = _.cloneDeep(data);
        this.setState({ UnsavedData: false });
    }
    // ------------------------------------------------------------------------------------------------
    onElementChanged = (dataPath: string, data: any, prevData: any, formData: any) => {
        this.props.SendPortEvent(PortKey.onSelectChange, JSON.stringify({
            jsonName: dataPath,
            elementData: data,
            previousData: prevData,
            formData: formData
        } as IOnSelectChangeSendData))
    }
    // ------------------------------------------------------------------------------------------------
    checkForStateUpdates(states = this.props.PortsStates) {

        // Are there any elements?
        if (_.has(states, PortKey.elements))
            this.updateState(
                PortKey.elements,
                states[PortKey.elements].State,
                states[PortKey.elements].LastStateStatus,
                states[PortKey.elements].Timestamp
            );

        // Are there any data?
        if (_.has(states, PortKey.data))
            this.updateState(
                PortKey.data,
                states[PortKey.data].State,
                states[PortKey.data].LastStateStatus,
                states[PortKey.data].Timestamp
            );
    }
    // ------------------------------------------------------------------------------------------------
    updateDataPaths(elements: SettingsGroup[], data: IData): IData {
        // Initialize data (if needs to).
        if (_.isEmpty(data))
            data = {};

        // Set paths that don't exist in data.
        if (elements != null && _.isArray(elements))
            for (let v of elements)
                if (v != null && _.isArray(v.elements))
                    for (let _v of v.elements) {
                        if (_v == null)
                            continue;

                        if (_v.type == SettingsType.Group ||
                            _v.type == SettingsType.GroupStr) {
                            // go to subgroups
                            data = this.updateDataPaths([_v], data);
                        } else if (!isDefined(_.get(data, _v.dataPath))) {
                            // Set path's data (if not defined).
                            // based on element type; initialize properly field
                            switch (_v.type) {
                                case SettingsType.ItemsMgmtDialog:
                                case SettingsType.ItemsMgmtDialogStr:
                                case SettingsType.Attachments:
                                case SettingsType.AttachmentsStr:
                                    // expects an array
                                    _.set(data, _v.dataPath, []);
                                    break;
                                case SettingsType.Highlights:
                                case SettingsType.HighlightsStr:
                                    // expects an array or an object
                                    if ((_v as SettingsElementHighlights<any>).isMulti)
                                        _.set(data, _v.dataPath, {});
                                    else
                                        _.set(data, _v.dataPath, []);
                                    break;
                                case SettingsType.Boolean:
                                case SettingsType.BooleanStr:
                                    // false is the default value
                                    _.set(data, _v.dataPath, false);
                                    break;
                                default:
                                    _.set(data, _v.dataPath, '');
                                    break;
                            }
                        }
                    }

        return data;
    }
    // ------------------------------------------------------------------------------------------------
    updateState(key: PortKey, state: any, status: StateStatus, ts: number = null) {
        const defaultValues = this.props.WidgetDefaultValues;

        // Select the target port key.
        switch (key) {
            case PortKey.elements: {
                let payload: SettingsGroup[] = null;
                if (status == StateStatus.NoData) {
                    // Get any default elements.
                    payload = _.cloneDeep(defaultValues[key]);
                }
                else if (status == StateStatus.Valid) {
                    // Get elements from port.
                    payload = state;
                }
                else
                    return;

                // Update data paths.
                let data = this.storageData;
                this._tmpData = this.updateDataPaths(
                    payload,
                    data
                );

                // Update elements.
                this.setState({ elements: payload });
            }
                break;

            case PortKey.data: {
                let data: any = {};

                // Update data only if they are new ones
                if (ts > this._dataTimestamp)
                    this._dataTimestamp = ts;
                else
                    return;


                if (status == StateStatus.NoData) {
                    // Get any default data.
                    data = _.cloneDeep(defaultValues[key]);
                }
                else if (status == StateStatus.Valid) {
                    // Get data from port.
                    data = _.cloneDeep(state);
                }
                else
                    return;

                // If no elements, attempt to read port.
                let elements = this.state.elements;
                if (!isDefined(elements)) {
                    const port = this
                        .props
                        .PortsStates[PortKey.elements];

                    if (isDefined(port) &&
                        port.LastStateStatus == StateStatus.Valid)
                        elements = port.State;
                }

                this._tmpData = this.updateDataPaths(
                    elements,
                    data
                );
                this._lastSendData = _.cloneDeep(this._tmpData);
                this.setState({ UnsavedData: false });
            }
                break;

            default:
                break;
        }
    }
    // ------------------------------------------------------------------------------------------------
    getButtons() {
        const settings = this.props.WidgetData;
        const submitBtnConf = settings.submitButton;
        const locMap = this.props.LocalizationMap;

        // Localize submit button's label (if visible).
        if (submitBtnConf.visible || settings.cancelButton) {
            const locKey = LocalizationKeys.submit;
            let submitBtnLabel = locMap[locKey];
            if (IsNullOrEmpty(submitBtnLabel))
                submitBtnLabel = submitBtnConf.label;

            const _saveBtnTheme = {
                label: submitBtnLabel,
                style: {
                    height: 36,
                    overflow: 'hidden',
                    borderRadius: 10,
                    display: submitBtnConf.visible
                        ? 'inline-block' : 'none'
                }
            };

            // Localize cancel button's label (if visible).
            if (settings.cancelButton) {
                const locKey = LocalizationKeys.cancel;
                let cancelBtnLabel = locMap[locKey];
                if (IsNullOrEmpty(cancelBtnLabel))
                    cancelBtnLabel = defaultLocalizationValues[locKey];

                // get theme for cancel button
                const _cancelBtnTheme = {
                    label: cancelBtnLabel,
                    style: {
                        height: 36,
                        borderRadius: 10,
                        overflow: 'hidden'
                    }
                };

                return <React.Fragment>
                    <RaisedButton
                        style={_cancelBtnTheme.style}
                        label={_cancelBtnTheme.label}
                        labelColor='white'
                        backgroundColor='#E57373'
                        key="Cancel-Button"
                        onClick={this.clearStorage}
                        disabled={!this.state.UnsavedData}
                        fullWidth
                    />
                    <RaisedButton
                        style={_saveBtnTheme.style}
                        label={_saveBtnTheme.label}
                        labelColor='white'
                        backgroundColor='#64B5F6'
                        key="Save-Button"
                        onClick={() => this.handleSubmitClick(false)}
                        disabled={!this.state.UnsavedData}
                        fullWidth
                    />
                </React.Fragment>
            }

            return <RaisedButton
                style={_saveBtnTheme.style}
                label={_saveBtnTheme.label}
                labelColor='white'
                backgroundColor='#64B5F6'
                key="Save-Button"
                onClick={() => this.handleSubmitClick(false)}
                disabled={!this.state.UnsavedData}
                fullWidth
            />
        }

        return null;
    }
    // ------------------------------------------------------------------------------------------------
    onButtonPressed = (element, data) => {
        this.props.SendPortEvent(PortKey.onButtonElementClick, {
            element: element,
            data: data
        });
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        let settings = this.props.WidgetData;
        let buttons = this.getButtons();

        let numberOfGridColumns = settings.gridColumns;
        if (this.props.width < 410) {
            numberOfGridColumns = 1;
        } else if (this.props.width < 820) {
            numberOfGridColumns = 2;
        } else if (this.props.width < 1230) {
            numberOfGridColumns = 4;
        }

        // Render list with elements.
        return <div style={{
            width: this.props.width,
            height: this.props.height,
            backgroundColor: settings.backgroundColor,
            color: settings.fontColor,
            overflowX: "hidden",
            overflowY: "auto",
            padding: settings.padding
        }}>
            <SettingsList
                key={"settings-list"}
                AutoSave={true}
                HideUnsavedDataHighlight={settings.submitButton.visible == false}
                Data={this.storageData}
                UnsavedData={this.state.UnsavedData}
                onSaveData={_.noop}
                onDataChange={this.onDataChange}
                SettingsMap={this.state.elements}
                onElementChanged={this.onElementChanged}
                onButtonPressed={this.onButtonPressed}
                numberOfGridColumn={numberOfGridColumns}
                gridGap={settings.gridGap}
                onHoverHighlight={settings.onHoverHighlight}
            />
            {buttons
                ? <div key={"settings-list-button"}
                    style={{ padding: '10px 16px' }}>
                    {buttons}
                </div>
                : null}
        </div>;
    }
    // ------------------------------------------------------------------------------------------------
}
// ----------------------------------------------------------------------------------------------------
class DynamicFormSettings extends React.PureComponent<IWidgetSettingsProps<IDynamicFormSettings>> {
    // ------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<IDynamicFormSettings>) { super(props); }
    // ------------------------------------------------------------------------------------------------
    render() {
        // Render list with settings.
        return <SettingsList
            AutoSave={true}
            Toolbar={this.props.SettingListToolbar}
            SettingsMap={[
                {
                    name: '',
                    elements: [
                        // Font color setting.
                        {
                            type: SettingsType.ColorPicker,
                            label: 'Font Color',
                            tooltip: 'Specifies the color of the text inside the form',
                            dataPath: 'fontColor',
                        },
                        // Background color setting.
                        {
                            type: SettingsType.ColorPicker,
                            label: 'Background Color',
                            tooltip: 'Sets the background color of the form',
                            dataPath: 'backgroundColor',
                        },
                        {
                            type: SettingsType.Slider,
                            label: 'Padding',
                            dataPath: 'padding',
                            minValue: 0,
                            maxValue: 30,
                            stepValue: 1,
                            valuePosfix: 'px'
                        },
                        {
                            type: SettingsType.Group,
                            name: 'Grid',
                            elements: [
                                {
                                    type: SettingsType.Slider,
                                    label: 'Gap',
                                    dataPath: 'gridGap',
                                    tooltip: 'Sets the gaps between rows and columns',
                                    minValue: 0,
                                    maxValue: 50,
                                    stepValue: 1,
                                    valuePosfix: 'px'
                                },
                                {
                                    type: SettingsType.Slider,
                                    label: 'Max Size',
                                    dataPath: 'gridColumns',
                                    tooltip: 'It defines the maximum number of columns',
                                    minValue: 1,
                                    maxValue: 6,
                                    stepValue: 1,
                                    valuePosfix: ''
                                }
                            ]
                        },
                        // Submit button setting.
                        {
                            type: SettingsType.Group,
                            name: 'Submit Button',
                            elements: [
                                {
                                    type: SettingsType.Boolean,
                                    label: 'Visibility',
                                    tooltip: 'Whether the submit button should be visible',
                                    dataPath: 'submitButton.visible'
                                },
                                {
                                    type: SettingsType.String,
                                    label: 'Label',
                                    tooltip: 'Label for the submit button',
                                    dataPath: 'submitButton.label',
                                    isHidden: d => !d.submitButton.visible
                                }
                            ]
                        },
                        // Auto submit setting.
                        {
                            type: SettingsType.Group,
                            name: 'Auto Submit',
                            elements: [
                                {
                                    type: SettingsType.Boolean,
                                    label: 'Enable',
                                    tooltip: 'Auto submit the form at specified time intervals',
                                    dataPath: 'autoSubmit.enable'
                                },
                                {
                                    type: SettingsType.DropDown,
                                    label: 'Unit of Time',
                                    tooltip: 'The unit of time for the time interval',
                                    dataPath: 'autoSubmit.unitOfTime',
                                    items: [
                                        {
                                            label: 'Second',
                                            value: UnitOfTime.sec
                                        },
                                        {
                                            label: 'Minute',
                                            value: UnitOfTime.min
                                        },
                                        {
                                            label: 'Hour',
                                            value: UnitOfTime.hour
                                        }
                                    ],
                                    isHidden: d => !d.autoSubmit.enable
                                },
                                {
                                    type: SettingsType.Number,
                                    label: 'Time Interval',
                                    tooltip: 'The interval on how often to auto submit the form',
                                    dataPath: 'autoSubmit.timeInterval',
                                    oneLine: true,
                                    minValue: 1,
                                    isHidden: d => !d.autoSubmit.enable
                                }
                            ]
                        },
                        // Cancel button setting.
                        {
                            type: SettingsType.Boolean,
                            label: 'Cancel Button',
                            tooltip: 'Whether the cancel button should be visible',
                            dataPath: 'cancelButton'
                        },
                        {
                            type: SettingsType.Boolean,
                            label: 'Hover Effect',
                            tooltip: 'Highlights elements when you mouse over them',
                            dataPath: 'onHoverHighlight',
                        }
                    ]
                }
            ]}
            Data={this.props.WidgetData}
            onSaveData={data => {
                const onSaveCb = this.props.onWidgetDataUpdate;
                if (isFunction(onSaveCb))
                    onSaveCb(data);
            }}
        />;
    }
    // ------------------------------------------------------------------------------------------------
}
// ----------------------------------------------------------------------------------------------------
const enum PortKey {
    elements = 'elements',
    data = 'data',
    submit = 'submit',
    autoSubmit = 'autosubmit',
    onSelectChange = 'onselectchange',
    onButtonElementClick = 'onButtonElementClick'
}
// ----------------------------------------------------------------------------------------------------

export class DynamicFormWidget extends IWidgetThingsClass<IDynamicFormSettings> {
    // ------------------------------------------------------------------------------------------------
    Render(): React.ReactNode { return <DynamicForm />; }
    // ------------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactNode {
        const parent = this.Parent;
        return <DynamicFormSettings
            key={'settings-' + key}
            NodeKey={this.NodeKey}
            ThingKey={this.ThingKey}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={data => this.WidgetDataUpdate(data)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null} />;
    }
    // ------------------------------------------------------------------------------------------------
    get ports(): WebThingPort[] {
        return [
            // Elements input port.
            new WebThingPort(
                {
                    Name: 'Elements',
                    PortKey: PortKey.elements,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'The form elements to capture user input',
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(PortKey.elements, data)
            ),
            // Data input port.
            new WebThingPort(
                {
                    Name: 'Data',
                    PortKey: PortKey.data,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Values to fill the form elements',
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(PortKey.data, data)
            ),
            // Submit output port.
            new WebThingPort({
                Name: 'Submit',
                PortKey: PortKey.submit,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'Outputs the data inside the form',
                State: ''
            }, WebThingPortType.Trigger),
            // Auto submit output port.
            new WebThingPort({
                Name: 'Auto Submit',
                PortKey: PortKey.autoSubmit,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'If auto submit is enabled, it outputs the data inside the form at the specified time intervals',
                State: ''
            }, WebThingPortType.Trigger),
            // Element change output port.
            new WebThingPort({
                Name: 'On Element Change',
                PortKey: PortKey.onSelectChange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'Outputs the JSON name and data of the changed element and the overall form\'s data',
                State: ''
            }, WebThingPortType.Trigger),
            // Button element have be clicked
            new WebThingPort({
                Name: 'On Button Element Click',
                PortKey: PortKey.onButtonElementClick,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'Outputs the button element and the form data.',
                State: ''
            }, WebThingPortType.Trigger)
        ] as WebThingPort[];
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing initialization.
        const webThing = new WebThing(
            this.WebModule,
            WidgetDescriptionToPlegmaThing(this.Description),
            this.ports);
        this.ThingKey = webThing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        // Get node and thing instances.
        const node = WebNode.GetInstanceKey(this.NodeKey);
        const thing = node.GetThing(this.ThingKey);

        // Ports setup.
        thing.Ports = {};
        _.forEach(this.ports, v => thing.AddPort(v));

        // Update thing.
        node.GetModule(this.WebModuleKey)
            .UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    GetDefaultWidgetData(): IDynamicFormSettings { return DynamicFormWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): IDynamicFormSettings {
        return {
            fontColor: 'rgba(0, 0, 0, 0.87)',
            backgroundColor: 'rgb(255, 255, 255)',
            cancelButton: false,
            submitButton: {
                visible: false,
                label: defaultLocalizationValues[LocalizationKeys.submit]
            },
            autoSubmit: {
                enable: false,
                unitOfTime: UnitOfTime.min,
                timeInterval: 1
            },
            gridGap: 5,
            gridColumns: 4,
            padding: 5,
            onHoverHighlight: false
        };
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // Widget descriptor.
        return {
            MinW: 1,
            MinH: 1,
            Key: 'DynamicFormWidget',
            Type: 'DynamicFormWidget',
            Title: 'Dynamic Form',
            Description: 'Represents a form with custom created components',
            Data: DynamicFormWidget.GetDefaultWidgetDataStatic(),
            IconURI: IconURIPath + '/png/dynamic_form.png'
        };
    }
    // ------------------------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // Validation for incoming event.
        switch (key) {
            case PortKey.elements:
                return elementsValidator().DataValidateString(value);
            case PortKey.data:
                return dataValidator().DataValidateString(value);
            default:
                return { valid: true, errors: '' };
        }
    }
    // ------------------------------------------------------------------------------------------------
    // GetDataPortKeyStatic(): string { return PortKey.data; }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let dict = {};

        // Expose widget title (if defined).
        const widgetTitle = this.Description.WidgetTitle;
        if (!IsNullOrEmpty(widgetTitle))
            dict[WidgetLocalizationKeys.Title] = widgetTitle;

        // Expose submit button's label (if defined).
        const submitBtnLabel = this.WidgetData.submitButton.label;
        if (!IsNullOrEmpty(submitBtnLabel))
            dict[LocalizationKeys.submit] = submitBtnLabel;

        // Expose cancel button's label.
        const locKey = LocalizationKeys.cancel;
        dict[locKey] = _.clone(defaultLocalizationValues[locKey]);
        return dict;
    }
    // ------------------------------------------------------------------------------------------------
}
// ----------------------------------------------------------------------------------------------------
const enum LocalizationKeys {
    submit = 'submit',
    cancel = 'cancel'
}
// ----------------------------------------------------------------------------------------------------
const defaultLocalizationValues = {
    [LocalizationKeys.submit]: 'Submit',
    [LocalizationKeys.cancel]: 'Cancel'
}
// ----------------------------------------------------------------------------------------------------